import { Controller } from "@hotwired/stimulus";
import { loadScript } from "@paypal/paypal-js";

// Global PayPal SDK loader - ensures script is loaded only once
let paypalSdkPromise = null;
const loadPayPalSDK = (config) => {
  if (!paypalSdkPromise) {
    paypalSdkPromise = loadScript({
      clientId: config.clientId,
      merchantId: config.merchantId,
      disableFunding: "card,credit",
      currency: config.currency,
    });
  }
  return paypalSdkPromise;
};

// Module-level mount guard. The BalanceComponent renders twice on most
// project pages (mobile-card + sidebar-card) and CSS hides one per breakpoint,
// but Stimulus mounts both regardless of visibility. The guard tracks which
// clientSecret already owns the active iframe so the second instance no-ops
// instead of mounting a duplicate Element against the same PaymentIntent.
let activeStripeClientSecret = null;

const setError = ($form, msg) => {
  const $errorElement = $form.find(".card-errors");

  $errorElement.html(`<div class="alert alert-danger alert-dismissible mt-0 mb-3">
    ${msg}
    <button type="button" class="close" data-dismiss="alert" aria-label="Close">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>`);
  $form.find("input[name='project_payment[paypal_order_id]']").val("");
  $form.find("input[name='project_payment[payment_type]']").val("");
  enableFormSubmission($form);
};

const disableFormSubmission = ($form) => {
  $form.find(":submit").prop("disabled", true);
  $form.find(".paypal-container").addClass("disabled");
};

const enableFormSubmission = ($form) => {
  $form.find(":submit").removeAttr("disabled");
  $form.find(".paypal-container").removeClass("disabled");
};

export default class extends Controller {
  static targets = [];

  connect() {
    this.alive = true;

    if (window.Stripe) {
      this.initStripe();
    }

    // Initialize PayPal with dynamic script loading
    this.initPayPal();

    this.handleTurboBeforeVisitBound = this.handleTurboBeforeVisit.bind(this);
    this.handleTurboBeforeCacheBound = this.handleTurboBeforeCache.bind(this);

    document.addEventListener(
      "turbo:before-visit",
      this.handleTurboBeforeVisitBound,
    );
    document.addEventListener(
      "turbo:before-cache",
      this.handleTurboBeforeCacheBound,
    );
  }

  disconnect() {
    this.alive = false;
    document.removeEventListener(
      "turbo:before-visit",
      this.handleTurboBeforeVisitBound,
    );
    document.removeEventListener(
      "turbo:before-cache",
      this.handleTurboBeforeCacheBound,
    );
    this.cleanupPayPal();
    this.cleanupStripe();
  }

  handleTurboBeforeVisit() {
    this.cleanupPayPal();
    this.cleanupStripe();
  }

  handleTurboBeforeCache() {
    this.cleanupPayPal();
    this.cleanupStripe();
  }

  cleanupPayPal() {
    if (this.paypalButtonsComponent) {
      try {
        this.paypalButtonsComponent.close();
      } catch (e) {
        console.warn("PayPal cleanup error:", e);
      } finally {
        this.paypalButtonsComponent = null;
      }
    }

    // Clear the PayPal container element to prevent DOM issues
    const paypalContainer = document.querySelector(
      `#paypal-${this.data.get("cardFieldId")}`,
    );
    if (paypalContainer) {
      paypalContainer.replaceChildren();
    }
  }

  cleanupStripe() {
    const $form = $(this.element);

    // Detach the namespaced submit handler so a Turbo cache restore + reconnect
    // doesn't leave two submit handlers firing concurrent confirmPayment.
    $form.off("submit.stripe-pay");

    // Reset the submit button to disabled so the cached snapshot doesn't
    // present an enabled button before the re-mounted Element fires `ready`.
    $form.find(":submit").prop("disabled", true);

    if (this.paymentElement) {
      try {
        this.paymentElement.unmount();
      } catch (e) {
        console.warn("Stripe cleanup error:", e);
      } finally {
        this.paymentElement = null;
      }

      // Clear the mount container in case unmount left a stale iframe behind
      // (mirrors the PayPal cleanup pattern).
      const mountTarget = document.querySelector(
        `#${this.data.get("cardFieldId")}`,
      );
      if (mountTarget) {
        mountTarget.replaceChildren();
      }
    }

    // Release the active-mount slot if we were the holder.
    if (this.ownsActiveMount) {
      activeStripeClientSecret = null;
      this.ownsActiveMount = false;
    }

    this.elements = null;
    this.stripe = null;
  }

  async initPayPal() {
    const clientId = this.data.get("paypalClientId");
    const merchantId = this.data.get("paypalMerchantId");

    if (!clientId || !merchantId) {
      return;
    }

    this.cleanupPayPal();

    const form = this.element;
    const $form = $(form);
    const amountInDollars = this.data.get("amountInDollars");
    const currency = this.data.get("paypalCurrency");

    try {
      const paypal = await loadPayPalSDK({
        clientId: clientId,
        merchantId: merchantId,
        currency: currency,
      });

      if (!paypal) {
        console.error("PayPal SDK failed to load");
        return;
      }

      this.paypalButtonsComponent = paypal.Buttons({
        style: { height: 43 },
        createOrder: function (data, actions) {
          disableFormSubmission($form);
          $form
            .find("input[name='project_payment[payment_type]']")
            .val("paypal");

          return actions.order.create({
            purchase_units: [
              {
                amount: { value: amountInDollars },
                payee: { merchant_id: merchantId },
              },
            ],
          });
        },
        onApprove: function (data, actions) {
          $form
            .find("input[name='project_payment[paypal_order_id]']")
            .val(data.orderID);

          $.ajax({
            type: "POST",
            url: $form.attr("action"),
            headers: {
              "X-CSRF-Token": Rails.csrfToken(),
            },
            data: $form.serialize(),
            success: function (response) {
              window.location.reload();
            },
            error: function (response) {
              setError($form, response.responseJSON.error);
            },
          });
        },
        onCancel: function (data, actions) {
          // Clear payment_type so a subsequent Stripe submit isn't routed
          // through the PayPal POST path with an empty paypal_order_id.
          $form.find("input[name='project_payment[payment_type]']").val("");
          enableFormSubmission($form);
        },
        onError: function (err) {
          setError($form, err);
        },
      });

      await this.paypalButtonsComponent.render(
        `#paypal-${this.data.get("cardFieldId")}`,
      );
    } catch (error) {
      console.error("Failed to load PayPal SDK script:", error);
    }
  }

  initStripe() {
    const clientSecret = this.data.get("stripeClientSecret");

    // No client secret = no payable PI on this page render (e.g.,
    // PayPal-only or fully-paid project). Stripe init is a no-op.
    if (!clientSecret) {
      return;
    }

    // BalanceComponent renders twice on every project page (mobile-card
    // inline, sidebar-card in the responsive navbar). Each viewport hides
    // one via CSS. Mount only on the currently-visible instance — DOM
    // order alone would mount into a hidden card at desktop widths.
    const mountTargetId = this.data.get("cardFieldId");
    const mountTarget = document.getElementById(mountTargetId);
    if (!mountTarget || mountTarget.offsetParent === null) {
      // Hidden at this breakpoint (display: none up the tree). Skip
      // mounting; the other instance owns the active iframe.
      return;
    }

    // Module-level guard: if another instance already mounted for this
    // clientSecret, no-op.
    if (activeStripeClientSecret === clientSecret) {
      return;
    }
    activeStripeClientSecret = clientSecret;
    this.ownsActiveMount = true;

    const form = this.element;
    const $form = $(form);
    const returnUrl = this.data.get("stripeReturnUrl");

    this.stripe = Stripe(this.data.get("stripePublishableKey"), {
      stripeAccount: this.data.get("stripeAccountId"),
    });

    this.elements = this.stripe.elements({
      clientSecret: clientSecret,
      // White input fields on the dark "Payment Information" card. Theme
      // "stripe" + variable overrides gives us white-filled inputs with
      // dark text and a primary action color matching the app's accent.
      appearance: {
        theme: "stripe",
        // Float labels INSIDE input fields. Sidesteps the dark-card-vs-
        // white-block contrast problem entirely: labels live on the
        // input's white background and inherit the input's dark text
        // color, so they're readable in any container.
        labels: "floating",
        variables: {
          fontFamily: "Open Sans, sans-serif",
          // colorPrimary controls Link branding (the "Secure, fast checkout
          // with Link" prompt + its chevron) and is a CSS variable Stripe
          // exposes inside the iframe. Setting it to white makes the prompt
          // legible on the dark "Payment Information" card. Doesn't affect
          // the "Pay Remaining" button — that's a Bootstrap btn-primary
          // outside the iframe.
          colorPrimary: "#ffffff",
          colorBackground: "#ffffff",
          colorText: "#32325d",
          colorDanger: "#fa755a",
          colorIconChevronDown: "#ffffff",
          borderRadius: "6px",
          spacingUnit: "4px",
        },
        rules: {
          // Tab labels (Card / Link toggle) render against the dark card,
          // so use a light foreground rather than the default dark.
          ".Tab": {
            color: "#ffffff",
            backgroundColor: "transparent",
            border: "1px solid rgba(255, 255, 255, 0.25)",
          },
          ".Tab:hover": {
            color: "#ffffff",
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
          ".Tab--selected": {
            color: "#32325d",
            backgroundColor: "#ffffff",
          },
          ".Label": {
            // Floating labels live inside the white input field, so
            // they need dark text (matching .Input). The label briefly
            // appears above the input as a placeholder hint when the
            // input is empty — at that point it's on the dark card,
            // but Stripe's floating-label modifier handles the contrast
            // automatically.
            color: "#32325d",
            fontWeight: "600",
          },
          // colorPrimary is white now (so the Link prompt reads on the
          // dark card), but input focus would inherit white-on-white and
          // be invisible. Restore a visible focus state that matches the
          // app's existing input focus shadow.
          ".Input:focus": {
            boxShadow:
              "0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08)",
          },
        },
      },
      fonts: [
        { cssSrc: "https://fonts.googleapis.com/css?family=Open+Sans" },
      ],
      locale: "auto",
    });

    this.paymentElement = this.elements.create("payment", {
      layout: { type: "tabs" },
      paymentMethodOrder: ["card"],
    });
    this.paymentElement.mount(`#${this.data.get("cardFieldId")}`);

    this.paymentElement.on("ready", () => {
      enableFormSubmission($form);
    });

    const stripe = this.stripe;
    const elements = this.elements;
    const controller = this;

    $form.on("submit.stripe-pay", async (e) => {
      // Only intercept Stripe submissions — PayPal handles its own POST.
      const paymentType = $form
        .find("input[name='project_payment[payment_type]']")
        .val();
      if (paymentType === "paypal") {
        return;
      }

      e.preventDefault();
      disableFormSubmission($form);
      $form.find("input[name='project_payment[payment_type]']").val("card");

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements: elements,
        confirmParams: {
          return_url: returnUrl,
        },
        redirect: "if_required",
      });

      // Turbo may have navigated away while we awaited Stripe — bail out
      // rather than redirect a stale form to the return URL.
      if (!controller.alive) {
        return;
      }

      if (error) {
        setError($form, error.message || "We couldn't process that payment.");
        return;
      }

      // Synchronous resolution: confirmPayment returned a paymentIntent and
      // did not redirect. Manually append the same query params Stripe
      // would have appended on a real redirect (payment_intent +
      // payment_intent_client_secret + redirect_status) so the host page
      // the customer was paying from can identify the row and render the
      // status banner. Webhook reconciliation still owns persistence.
      if (paymentIntent) {
        const url = new URL(returnUrl);
        url.searchParams.set("payment_intent", paymentIntent.id);
        if (paymentIntent.client_secret) {
          url.searchParams.set(
            "payment_intent_client_secret",
            paymentIntent.client_secret,
          );
        }
        url.searchParams.set(
          "redirect_status",
          paymentIntent.status === "succeeded" ? "succeeded" : "processing",
        );
        window.location.href = url.toString();
      }
    });
  }
}
